import React, { useEffect, useState } from "react";
import {
    Stack,
    Text,
    ensure,
    FormValidationSummary,
    ValidationResult,
    validationFailed,
    createErrorFailure,
    ConfirmDialog
} from "sprint-common-web";

import { useAssetsManageMovieApi } from "../../../api/useAssetsManageMovieApi";
import { ValidateMergeMovieVersionsRequest } from "../../../api/ValidateMergeMovieVersionsRequest";
import { getMergeMovieVersionText } from "../MovieVersionListing";
import { MovieVersionListingItem } from "../MovieVersionListingItem";

interface MergeMovieVersionsConfirmationModalProps {
    movieId: string;
    targetVersion?: MovieVersionListingItem;
    orphanedVersion?: MovieVersionListingItem;

    isOpen: boolean;
    isConfirmationDisabled: boolean;
    onConfirm: () => Promise<void>;
    onDismiss: () => void;
}

export const MergeMovieVersionsConfirmationModal = (props: MergeMovieVersionsConfirmationModalProps): JSX.Element => {
    const { validateMergeVersions } = useAssetsManageMovieApi();

    const [isValidationPending, setIsValidationPending] = useState(false);
    const [validationResult, setValidationResult] = useState<ValidationResult>();

    useEffect(() => {
        setValidationResult(undefined);

        if (!props.isOpen) return;

        if (isAnyVersionMissing(props)) return;

        const { movieId, targetVersion, orphanedVersion } = props;
        ensure(targetVersion);
        ensure(orphanedVersion);

        setIsValidationPending(true);

        validateMergeVersions(movieId, createRequest(targetVersion, orphanedVersion))
            .then((res) => {
                setValidationResult(res);
            })
            .catch((err) => {
                const failure = validationFailed([createErrorFailure(JSON.stringify(err))]);
                setValidationResult(failure);
            })
            .finally(() => setIsValidationPending(false));
    }, [props.isOpen]);

    const isAnyVersionMissing = ({ targetVersion, orphanedVersion }: MergeMovieVersionsConfirmationModalProps) =>
        !targetVersion || !orphanedVersion;

    if (isAnyVersionMissing(props)) return <></>;

    const isConfirmButtonDisabled = props.isConfirmationDisabled || isValidationPending || !validationResult?.isValid;

    return (
        <ConfirmDialog
            title="Merge versions"
            text="You are about to merge:"
            isBlocking={true}
            hidden={!props.isOpen}
            confirm={{ onClick: props.onConfirm, disabled: isConfirmButtonDisabled }}
            cancel={{ onClick: props.onDismiss }}
            width={800}
        >
            <Stack tokens={{ childrenGap: 10 }} styles={{ root: { minHeight: 130 } }}>
                <ConfirmationDetails target={props.targetVersion} orphan={props.orphanedVersion} />
                <FormValidationSummary validationResult={validationResult} isValidating={isValidationPending} />
            </Stack>
        </ConfirmDialog>
    );
};

interface ConfirmationDetailsProps {
    target?: MovieVersionListingItem;
    orphan?: MovieVersionListingItem;
}

const indentationStyles = { root: { textIndent: "20px" } };

const ConfirmationDetails = ({ target, orphan }: ConfirmationDetailsProps): JSX.Element => {
    if (!target || !orphan) return <></>;

    return (
        <Stack tokens={{ childrenGap: 10 }}>
            <Text styles={indentationStyles}>
                <b>{getMergeMovieVersionText(orphan, false)}</b> (orphan)
            </Text>
            <Text>into</Text>
            <Text styles={indentationStyles}>
                <b>{getMergeMovieVersionText(target, false)}</b> (target).
            </Text>
        </Stack>
    );
};

const createRequest = (
    targetVersion: MovieVersionListingItem,
    orphanedVersion: MovieVersionListingItem
): ValidateMergeMovieVersionsRequest => {
    return {
        targetVersionId: targetVersion.id,
        orphanedVersionId: orphanedVersion.id
    };
};
